import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import type { ReviewPreviewCardPayload } from '@noths/polaris-design-system-candidate-components';
import { ReviewPreviewCard } from '@noths/polaris-design-system-candidate-components';
import { Button } from '@noths/polaris-design-system-core-components';
import type { Review } from '@noths/polaris-design-system-ooux-interfaces';

import * as copy from '../constants/copy';
import { usePreviewScrollTracking } from '../hooks/usePreviewScrollTracking';
import * as styles from './CustomersReviewsPreview.styles';

export interface CustomersReviewsPreviewProps {
  /** The moment the cards are read, so their relative dates can be pinned in stories and specs. */
  date?: Date;
  /** True once the fetch has failed, so the row says so rather than staying blank. */
  error?: boolean;
  /** Where the "view all" button leads: the reviews drawer, at the list. */
  href: string;
  /** Where a card's controls lead: the drawer opened on that one review. */
  hrefForReview: (reviewId: string) => string;
  onControlClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    payload: ReviewPreviewCardPayload,
  ) => void;
  /** Fires once per settled swipe of the row. There are no arrows to press. */
  onScroll?: (direction: 'next' | 'previous') => void;
  onVisible?: () => void;
  /** `null` before the first page settles, which is what renders the skeleton. */
  reviews: Review[] | null;
  /** The product's whole review count, not `reviews.length`. */
  totalCount: number;
}

/** Named rather than counted so each skeleton card has a stable key. */
const noop = () => undefined;

const SKELETON_KEYS = ['first', 'second', 'third', 'fourth'];

/**
 * The most recent reviews on the product page, and the way through to the rest. Every
 * control leads into the drawer, nothing expands in place: "view all" to the list at
 * `href`, and a card's "Read more", photos and reply row to that review at
 * `hrefForReview(id)`. The row has no arrows: it scrolls where four cards do not fit and
 * shares the row between them where they do; see `PREVIEW_REVIEW_COUNT`.
 */
export const CustomersReviewsPreview = ({
  date,
  error = false,
  href,
  hrefForReview,
  onControlClick,
  onScroll,
  onVisible,
  reviews,
  totalCount,
}: CustomersReviewsPreviewProps) => {
  const trackRef = usePreviewScrollTracking(onScroll);
  const rootRef = useImpressionRef<HTMLElement>(onVisible ?? noop);

  return (
    <section aria-labelledby={styles.HEADING_ID} css={styles.root} ref={rootRef}>
      <header css={styles.header}>
        <h2 css={styles.heading} id={styles.HEADING_ID}>
          {copy.HEADING}
        </h2>
      </header>

      {error && <p css={styles.message}>{copy.ERROR_REVIEWS}</p>}

      {!error && reviews === null && (
        <>
          <p css={styles.message}>{copy.LOADING_REVIEWS}</p>
          <ul aria-hidden css={styles.track} data-testid="reviews-preview-skeleton">
            {SKELETON_KEYS.map((key) => (
              <li css={styles.skeletonItem} key={key} />
            ))}
          </ul>
        </>
      )}

      {!error && reviews?.length === 0 && <p css={styles.message}>{copy.NO_REVIEWS}</p>}

      {!error && reviews && reviews.length > 0 && (
        <ul css={styles.track} data-testid="reviews-preview-track" ref={trackRef}>
          {reviews.map((review) => (
            <li css={styles.item} key={review.id}>
              <ReviewPreviewCard
                date={date}
                href={hrefForReview(review.id)}
                onControlClick={onControlClick}
                review={review}
              />
            </li>
          ))}
        </ul>
      )}
      {totalCount > 0 && (
        <footer css={styles.footer}>
          <Button href={href} secondary>
            {copy.seeAllReviewsLabel(totalCount)}
          </Button>
        </footer>
      )}
    </section>
  );
};

CustomersReviewsPreview.displayName = 'CustomersReviewsPreview';
